import React, { useState, useRef, useEffect, Fragment } from 'react'
import {
  PlusIcon,
  PaperAirplaneIcon,
  TrashIcon,
  MicrophoneIcon,
  ArrowUpIcon,
  ClipboardIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/outline'
import { quickSuggestions, staffInfo } from '../data/chatTemplates'
import { chatbotConfig } from '../config/chatbot'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import './MarkdownChat.css'
import TextareaAutosize from 'react-textarea-autosize'
import { Popover, Transition } from '@headlessui/react'
import { BoltIcon, XMarkIcon } from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'

interface ChatWindowProps {
  isOpen: boolean
  onClose: () => void
}

interface Message {
  id: string
  content: string
  isBot: boolean
  timestamp: string
  isTyping?: boolean
  showTime?: boolean
  hasFinishedTyping?: boolean
}

// Component hiệu ứng "đang suy nghĩ"
const ThinkingIndicator = () => {
  return (
    <div className="flex items-center space-x-3 p-3 bg-white rounded-2xl shadow-sm max-w-[80%]">
      <div className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center animate-pulse">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          className="w-4 h-4 text-white"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">chờ xíu nhé...</span>
        <div className="flex space-x-1 mt-1">
          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"></div>
        </div>
      </div>
    </div>
  )
}

// Component hiệu ứng typing
const TypingMessage: React.FC<{ content: string; onFinishTyping?: () => void }> = ({ content, onFinishTyping }) => {
  const [displayedContent, setDisplayedContent] = useState('')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showCursor, setShowCursor] = useState(true)

  useEffect(() => {
    // Hiệu ứng nhấp nháy con trỏ
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev)
    }, 500)

    return () => clearInterval(cursorInterval)
  }, [])

  useEffect(() => {
    if (currentIndex < content.length) {
      const timeout = setTimeout(() => {
        setDisplayedContent(prev => prev + content[currentIndex])
        setCurrentIndex(prev => prev + 1)
      }, 8)
      return () => clearTimeout(timeout)
    } else if (onFinishTyping) {
      onFinishTyping()
    }
  }, [content, currentIndex, onFinishTyping])

  return (
    <div className="relative">
      <div className="flex items-center space-x-2 mb-2">
        <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            className="w-3 h-3 text-white"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        </div>
        <span className="text-xs text-gray-500">Mai đang trả lời...</span>
      </div>
      <p className="text-sm whitespace-pre-line">
        {displayedContent}
        <span
          className={`inline-block w-0.5 h-4 ml-0.5 -mb-0.5 bg-current ${showCursor ? 'opacity-100' : 'opacity-0'
            } transition-opacity duration-100`}
        ></span>
      </p>
    </div>
  )
}

// Component render markdown đẹp cho message bot
const MarkdownMessage: React.FC<{ content: string }> = ({ content }) => (
  <div className="markdown-chat-message">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
  </div>
)

// Thêm global styles cho scrollbar
const globalStyles = `
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb {
    background: #CBD5E1;
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #94A3B8;
  }
  * {
    scrollbar-width: thin;
    scrollbar-color: #CBD5E1 transparent;
  }
`

export const ChatWindow: React.FC<ChatWindowProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<Message[]>(() => {
    // Đọc tin nhắn từ localStorage khi khởi tạo
    const savedMessages = localStorage.getItem('chat_messages');
    if (savedMessages) {
      const parsedMessages = JSON.parse(savedMessages);
      // Đảm bảo tin nhắn có đúng định dạng và các thuộc tính cần thiết
      return parsedMessages.map((msg: any) => ({
        ...msg,
        timestamp: msg.timestamp || new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }),
        isTyping: msg.isBot && !msg.hasFinishedTyping, // Chỉ typing nếu là tin nhắn bot và chưa hoàn thành
        showTime: false
      }));
    }
    // Nếu không có tin nhắn đã lưu, trả về tin nhắn chào mừng mặc định
    return [{
      id: '1',
      content: `${staffInfo.nickname} ơi! có cần tui hỗ trợ gì không nè? đừng ngại hen tui luôn bên bạn mọi lúc hen 😊`,
      isBot: true,
      timestamp: new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }),
      isTyping: true,
      showTime: false,
      hasFinishedTyping: false
    }];
  });

  // Lưu tin nhắn vào localStorage mỗi khi messages thay đổi
  useEffect(() => {
    // Lưu tin nhắn với trạng thái typing và showTime được reset
    const messagesToSave = messages.map(msg => ({
      ...msg,
      isTyping: false, // Không lưu trạng thái typing
      showTime: false // Không lưu trạng thái hiển thị thời gian
    }));
    localStorage.setItem('chat_messages', JSON.stringify(messagesToSave));
  }, [messages]);

  const [selectedMsgId, setSelectedMsgId] = useState<string | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isThinking, setIsThinking] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContentRef = useRef<HTMLDivElement>(null)
  const [recentTopics, setRecentTopics] = useState<string[]>(() => {
    const saved = localStorage.getItem('recent_topics');
    return saved ? JSON.parse(saved) : [];
  });

  // Các topic phổ biến nhất (id)
  const topSuggestions = ['1', '2', '3', '4', '5', '6'];
  const getSmartSuggestions = () => {
    // Lấy 1-2 topic vừa xem gần nhất (không cần phải thuộc topSuggestions)
    const recent = recentTopics.slice(0, 2);
    // Lấy các topic phổ biến còn lại, loại bỏ những cái đã có trong recent
    const rest = topSuggestions.filter(id => !recent.includes(id));
    // Kết hợp lại, tổng cộng 6 mục
    return [...recent, ...rest].slice(0, 6);
  };
  const displayedSuggestions = quickSuggestions;

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (selectedMsgId) {
      const timer = setTimeout(() => {
        setSelectedMsgId(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [selectedMsgId])

  useEffect(() => {
    // Thêm global styles
    const styleSheet = document.createElement("style")
    styleSheet.innerText = globalStyles
    document.head.appendChild(styleSheet)

    return () => {
      document.head.removeChild(styleSheet)
    }
  }, [])

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }

  const handleSend = async (content: string) => {
    if (!content.trim()) return

    const now = new Date()
    // Thêm tin nhắn của người dùng
    const userMessage: Message = {
      id: Date.now().toString(),
      content: content,
      isBot: false,
      timestamp: now.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }),
      showTime: false
    }
    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsThinking(true)

    try {
      const response = await fetch(`${chatbotConfig.apiConfig.endpoint}/${chatbotConfig.apiConfig.modelId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${chatbotConfig.apiConfig.token}`
        },
        body: JSON.stringify({
          question: content,
          overrideConfig: {
            ...chatbotConfig.apiConfig.overrideConfig,
            sessionId: staffInfo.id // Thêm sessionId từ user đang login
          }
        })
      });

      const data = await response.json();
      
      setIsThinking(false)

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: data.text || 'Xin lỗi, tôi không thể xử lý câu hỏi của bạn lúc này.',
        isBot: true,
        timestamp: new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }),
        isTyping: true,
        showTime: false,
        hasFinishedTyping: false
      }
      setMessages(prev => [...prev, botMessage])
    } catch (error) {
      console.error('Error calling chatbot API:', error);
      setIsThinking(false)
      
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'Xin lỗi, đã có lỗi xảy ra khi xử lý câu hỏi của bạn. Vui lòng thử lại sau.',
        isBot: true,
        timestamp: new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }),
        isTyping: true,
        showTime: false,
        hasFinishedTyping: false
      }
      setMessages(prev => [...prev, botMessage])
    }
  }

  const toggleMessageTime = (messageId: string) => {
    setMessages(prev => prev.map(message =>
      message.id === messageId
        ? { ...message, showTime: !message.showTime }
        : message
    ))
  }

  const handleQuickSuggestion = (suggestion: any, useMeaning = false) => {
    setRecentTopics(prev => {
      const newRecent = [suggestion.id, ...prev.filter(id => id !== suggestion.id)];
      localStorage.setItem('recent_topics', JSON.stringify(newRecent.slice(0, 5)));
      return newRecent.slice(0, 5);
    });
    handleSend(useMeaning && suggestion.meaning ? suggestion.meaning : suggestion.title);
    setShowQuickActions(false);
  }

  const handleFinishTyping = (messageId: string) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId
        ? { ...msg, isTyping: false, hasFinishedTyping: true }
        : msg
    ));
    // Scroll xuống sau khi tin nhắn hoàn thành typing
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  // Scroll xuống khi có tin nhắn mới
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage.isTyping) {
        scrollToBottom();
      }
    }
  }, [messages]);

  // Scroll xuống khi popup được mở
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        scrollToBottom();
      }, 100); // Đợi 100ms để đảm bảo DOM đã được render
    }
  }, [isOpen]);

  const [showConfirmClear, setShowConfirmClear] = useState(false)

  const handleClearChat = () => {
    setShowConfirmClear(false)
    localStorage.removeItem('chat_messages')
    window.location.reload()
  }

  const [isRecording, setIsRecording] = useState(false)
  let recognition: any = null

  const handleVoiceToText = () => {
    if (isThinking || isRecording) return;
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Trình duyệt của bạn không hỗ trợ voice-to-text!');
      return;
    }
    setIsRecording(true)
    // @ts-ignore
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognition = new SpeechRecognition();
    recognition.lang = 'vi-VN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInputValue(transcript);
      setIsRecording(false);
    };
    recognition.onerror = (event: any) => {
      setIsRecording(false);
      alert('Không nhận diện được giọng nói. Vui lòng thử lại!');
    };
    recognition.onend = () => {
      setIsRecording(false);
    };
    recognition.start();
  }

  const [showQuickActions, setShowQuickActions] = useState(false)

  // Đóng popup icon khi click ra ngoài
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.user-msg-action')) {
        setSelectedMsgId(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [isFullScreen, setIsFullScreen] = useState(false)

  if (!isOpen) return null;

  const outerClass = isMobile
    ? `fixed inset-0 z-[9999] bg-white flex flex-col transition-all duration-300 ${isFullScreen ? '' : 'h-[70vh] top-auto bottom-0 rounded-t-2xl shadow-2xl'}`
    : `fixed left-1/2 top-0 -translate-x-1/2 w-[460px] bg-white shadow-2xl overflow-hidden z-[9999] flex flex-col transition-all duration-300 ${isFullScreen ? 'h-[100vh]' : 'h-[70vh] top-auto bottom-0 rounded-t-xl'}`;

  const innerClass = isMobile
    ? 'relative bg-white flex flex-col w-full h-full rounded-t-xl shadow-2xl'
    : 'relative bg-white flex flex-col w-full h-full rounded-t-xl shadow-2xl';

  return (
    <div className={outerClass}>
      <div className={innerClass}>
        {/* Header */}
        <div className={`px-4 py-3 flex justify-between items-center bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-300 relative ${isFullScreen ? 'rounded-t-none' : 'rounded-t-xl'}`}>
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center">
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-md font-semibold text-white">Mai - Trợ Lý Nội Bộ</h3>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  strokeWidth={1}
                  stroke="currentColor"
                  className="w-6 h-6 text-white animate-bounce"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 gap-2 absolute right-4 top-1">
            <button
              onClick={() => setShowConfirmClear(true)}
              className="text-white/80 hover:text-white transition-colors p-1  mt-1 rounded-full hover:bg-white/10"
              title="Xóa lịch sử chat"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsFullScreen(v => !v)}
              className="text-white/80 hover:text-white transition-colors p-1 mt-1 rounded-full border border-white/20 hover:bg-white/10"
              title={isFullScreen ? 'Thu nhỏ' : 'Phóng to'}
            >
              {isFullScreen ? (
                <ArrowsPointingInIcon className="w-5 h-5" />
              ) : (
                <ArrowsPointingOutIcon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Chat content */}
        <div
          ref={chatContentRef}
          className="flex-1 p-4 overflow-y-auto space-y-4 bg-gray-50 pt-4 pb-12">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className={`rounded-2xl px-4 py-2 ${message.isBot
                    ? 'bg-white shadow-lg shadow-gray-200/50 max-w-[100%] relative'
                    : 'bg-blue-600 text-white max-w-[80%] user-msg-action'
                  }`}
              >
                <div>
                  {message.isBot ? (
                    <div className="flex flex-col w-full ">
                      {/* Nút copy ở góc trên phải */}
                      <button
                        className="absolute top-2 right-2 rounded hover:bg-gray-100"
                        title="Sao chép nội dung"
                        onClick={e => {
                          e.stopPropagation();
                          if (navigator.clipboard && window.isSecureContext) {
                            navigator.clipboard.writeText(message.content);
                          } else {
                            const textarea = document.createElement('textarea');
                            textarea.value = message.content;
                            textarea.style.position = 'fixed';
                            textarea.style.left = '-9999px';
                            document.body.appendChild(textarea);
                            textarea.focus();
                            textarea.select();
                            document.execCommand('copy');
                            document.body.removeChild(textarea);
                          }
                          toast.success('Đã sao chép', { duration: 1200, position: 'top-center', className: 'text-sm, mt-5' });
                        }}
                      >
                        <ClipboardIcon className="w-4 h-4 text-gray-300 hover:text-blue-500" />
                      </button>
                      <div className="flex-1">
                        <div style={{width: '100%'}}>
                          {message.isTyping && !message.hasFinishedTyping ? (
                            <TypingMessage
                              content={message.content}
                              onFinishTyping={() => handleFinishTyping(message.id)}
                            />
                          ) : (
                            <div className="items-start w-full">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 mt-2 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow">
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v2m6.364 1.636l-1.414 1.414M22 12h-2M19.364 19.364l-1.414-1.414M12 22v-2M4.636 19.364l1.414-1.414M2 12h2M4.636 4.636l1.414 1.414M8 12a4 4 0 108 0 4 4 0 00-8 0z" />
                                  </svg>
                                </div>
                                <span className="text-xs text-gray-400 ml-2 mt-2">Mai AI - {message.timestamp}</span>
                              </div>
                              <div className="ml-2 flex-1">
                                <MarkdownMessage content={message.content} />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-line">{message.content}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
          {isThinking && (
            <div className="flex justify-start">
              <ThinkingIndicator />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="m-4 mt-2 bg-white rounded-b-2xl" style={{paddingBottom: 'env(safe-area-inset-bottom)'}}>
          <div className="flex items-center gap-2">
            <button
              onClick={handleVoiceToText}
              className={`m-2 rounded-full transition-all duration-200 ${isThinking || isRecording ? 'bg-gray-100 text-gray-400' : 'hover:bg-blue-100 text-blue-600'}`}
              disabled={isThinking || isRecording}
              title={isRecording ? 'Đang ghi âm...' : 'Nhấn để nói'}
            >
              <MicrophoneIcon className={`w-6 h-6 ${isRecording ? 'animate-pulse text-red-500' : ''}`} />
            </button>
            <div className="relative flex-1">
              <TextareaAutosize
                minRows={1.5}
                maxRows={5}
                maxLength={400}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value.slice(0, 400))}
                placeholder={`${staffInfo.fullName} cần hỏi gì nè...?`}
                className="w-full pr-12 pl-4 py-2 rounded-xl border border-gray-200 border-2 focus:outline-none focus:border-blue-500 bg-white text-sm resize-none transition-all"
                disabled={isThinking}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (inputValue.trim()) handleSend(inputValue);
                  }
                }}
                style={{ paddingRight: 44, paddingLeft: 16, boxSizing: 'border-box' }}
              />
              <button
                onClick={() => handleSend(inputValue)}
                disabled={!inputValue.trim() || isThinking}
                className={`absolute right-2 bottom-0.5 -translate-y-1/2 p-2 rounded-full ${inputValue.trim() && !isThinking ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300'} transition-colors flex items-center justify-center`}
                style={{ minWidth: 24, minHeight: 24, height: 30, width: 30, pointerEvents: (!inputValue.trim() || isThinking) ? 'none' : 'auto' }}
              >
                <ArrowUpIcon className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions - Gợi ý nhanh */}
        {showQuickActions && (
          <div className="absolute inset-0 z-40">
            <div
              className="absolute inset-0 bg-black opacity-50"
              onClick={() => setShowQuickActions(false)}
            />
          </div>
        )}
        <div className="absolute bottom-24 right-5 z-50">
          {showQuickActions && (
            <div className="flex flex-col gap-3 mb-3">
              {displayedSuggestions.map((suggestion, idx) => {
                const Icon = suggestion.icon;
                return (
                  <button
                    key={suggestion.id}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow hover:bg-blue-50 transition"
                    onClick={() => handleQuickSuggestion(suggestion)}
                  >
                    {Icon && <Icon className="w-5 h-5 text-blue-500" />}
                    <span className="text-sm font-medium">{suggestion.title}</span>
                  </button>
                );
              })}
            </div>
          )}
          <button
            className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center border border-blue-200 hover:bg-blue-100 transition"
            onClick={() => setShowQuickActions((v) => !v)}
          >
            {showQuickActions ? (
              <XMarkIcon className="w-6 h-6 text-blue-500" />
            ) : (
              <BoltIcon className="w-6 h-6 text-blue-500" />
            )}
          </button>
        </div>
      </div>

      {/* Confirm clear modal */}
      {showConfirmClear && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-xs w-full">
            <div className="mb-4">
              <div className="font-semibold mb-2">Xóa lịch sử Chat?</div>
              <div className="text-sm text-gray-600">Bạn có chắc muốn xóa toàn bộ lịch sử chat không?</div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setShowConfirmClear(false)}
              >Hủy</button>
              <button
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                onClick={handleClearChat}
              >Xóa</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 